import { pojemnoscZestawuKwh } from './podsumowanieZestawu'
/**
 * Przeliczenie kosztu zestawu na kwotę dla klienta końcowego: marża + VAT.
 *
 * ═══ SKĄD SIĘ BIORĄ TE LICZBY ═══
 * Wszystkie składniki wchodzą tu jako BRUTTO w stawce sklepu (`vatSprzetu`,
 * domyślnie 23%) — tak podają ceny sklepy i tak zbudowane są nasze cenniki
 * konstrukcji i montażu. Rachunek sprowadza je do netto i dolicza stawkę
 * właściwą dla danej pozycji, bo instalacja PV na budynku mieszkalnym ma
 * obniżoną stawkę na USŁUGĘ MONTAŻU, a nie na towar.
 *
 * Szczegóły podziału stawek — przy `przeliczWycene` niżej.
 *
 * ═══ NIEZMIENNIK, KTÓRY MUSI ZOSTAĆ ═══
 * Przy równych stawkach i zerowej marży kwota końcowa jest CO DO GROSZA taka
 * sama jak przed wprowadzeniem ustawień firmowych. Ustawienia mają zmieniać
 * wycenę tylko wtedy, gdy firma świadomie coś ustawi — nikt nie może zobaczyć
 * innej sumy tylko dlatego, że się zalogował. Pilnuje tego test.
 *
 * Zaokrąglamy DOPIERO na końcu, na kwocie brutto. Zaokrąglanie po drodze
 * rozjeżdża sumę o kilka groszy względem rachunku sprawdzonego kalkulatorem,
 * a to dokument, o który klient dopytuje.
 */

/** Próg pasujący do mocy instalacji. Domknięty od dołu, otwarty od góry. */
export const progDlaMocy = (progi, kwp) => {
  const m = Number(kwp)
  if (!Array.isArray(progi) || !Number.isFinite(m) || m <= 0) return null
  return progi.find((p) => {
    const od = Number(p.od)
    const doGory = p.do == null ? Infinity : Number(p.do)
    return m >= od && m < doGory
  }) || null
}

/**
 * Liczba z pola progu albo `null`, gdy pole jest puste.
 *
 * `Number(null)` i `Number('')` dają ZERO, nie NaN — więc bez tego sprawdzenia
 * nieuzupełniona stawka montażu przechodziłaby dalej jako „0 zł/kWp", czyli
 * montaż za darmo na ofercie dla klienta. Pusto ma znaczyć „nie ustawiono".
 */
const liczbaAlboNull = (wartosc) => {
  if (wartosc === null || wartosc === undefined || wartosc === '') return null
  const n = Number(wartosc)
  return Number.isFinite(n) ? n : null
}

/** Stawka montażu za kWp z tabeli firmy albo `null`, gdy nie ma progu. */
export const stawkaMontazuZProgow = (progi, kwp) => {
  const prog = progDlaMocy(progi, kwp)
  return prog ? liczbaAlboNull(prog.wartosc) : null
}

/**
 * Marża doliczana do kwoty NETTO.
 *
 * `tryb === 'amount'` → kwota w złotych netto z progu.
 * `tryb === 'percent'` → procent od kwoty netto.
 *
 * Pusty próg (albo brak progu dla tej mocy) znaczy „firma nic tu nie ustawiła"
 * i daje zero. Świadomie NIE zgadujemy: marża wpisana za kogoś trafiłaby wprost
 * na ofertę pod jego nazwiskiem.
 */
export const marzaNetto = ({ progi, tryb, kwp, nettoZl }) => {
  const prog = progDlaMocy(progi, kwp)
  if (!prog) return 0

  if (tryb === 'percent') {
    const procent = liczbaAlboNull(prog.procent)
    if (procent == null || procent <= 0) return 0
    return (Number(nettoZl) || 0) * (procent / 100)
  }

  const kwota = liczbaAlboNull(prog.kwota)
  return kwota != null && kwota > 0 ? kwota : 0
}

/**
 * Pełny rachunek: od naszych cen brutto do kwoty dla klienta.
 *
 * ═══ SKŁADNIKI MAJĄ RÓŻNE STAWKI VAT ═══
 * Instalacja PV na budynku mieszkalnym to usługa z obniżoną stawką, ale obniżka
 * dotyczy USŁUGI MONTAŻU, a nie towaru. Dlatego rachunek nie może mieć jednej
 * stawki na całość:
 *
 *   - sprzęt, dostawa, konstrukcja  → `vatSprzetu`  (zwykle 23%)
 *   - montaż (robocizna), też magazynu → `vatMontazu`  (zwykle 8%)
 *   - marża (instalacji i magazynu)  → BEZ VAT, doliczana jako brutto
 *   - usługi dodatkowe              → cena NETTO i własna stawka każdej usługi
 *
 * KAŻDY składnik wchodzi tu jako BRUTTO w stawce źródłowej (`vatSprzetu`) — bo
 * tak podają ceny sklepy i tak zbudowane są nasze cenniki konstrukcji i montażu.
 * Najpierw sprowadzamy wszystko do netto, potem doliczamy VAT właściwy dla
 * danej pozycji. Dla sprzętu obie operacje się znoszą i kwota zostaje ta sama;
 * różnicę robi wyłącznie montaż.
 *
 * ═══ NIEZMIENNIK ═══
 * Przy `vatMontazu === vatSprzetu` i zerowej marży suma jest CO DO GROSZA taka
 * sama jak przed wprowadzeniem ustawień firmowych. Gość i konto prywatne nie
 * mogą zobaczyć innej kwoty tylko dlatego, że ktoś inny coś sobie ustawił.
 * Pilnuje tego test.
 *
 * Zaokrąglamy DOPIERO na samym końcu. Zaokrąglanie każdej pozycji osobno
 * rozjeżdża sumę o kilka groszy względem rachunku sprawdzonego kalkulatorem,
 * a to dokument, o który klient dopytuje.
 */
export const przeliczWycene = ({
  sprzetZl = 0,
  dostawaZl = 0,
  konstrukcjaZl = 0,
  montazZl = 0,
  kwp,
  progiMarzy = [],
  trybMarzy = 'amount',
  vatSprzetu = 23,
  vatMontazu = 23,
  kwh = null,
  magazynZl = 0,
  montazMagazynuZl = 0,
  progiMarzyMagazynu = [],
  uslugi = [],
}) => {
  const stawkaSprzetu = Number.isFinite(Number(vatSprzetu)) ? Number(vatSprzetu) : 0
  const stawkaMontazu = Number.isFinite(Number(vatMontazu)) ? Number(vatMontazu) : 0

  const mnoznikZrodlowy = 1 + stawkaSprzetu / 100
  const naNetto = (brutto) => {
    const kwota = Number(brutto) || 0
    return mnoznikZrodlowy > 0 ? kwota / mnoznikZrodlowy : kwota
  }

  // Netto poszczególnych grup — rozdzielone, bo dostają różne stawki z powrotem.
  const nettoTowar = naNetto(sprzetZl) + naNetto(dostawaZl) + naNetto(konstrukcjaZl)
  const nettoMontazPv = naNetto(montazZl)
  // Montaż magazynu to ta sama usługa montażu: wchodzi jak montaż PV i dostaje stawkę montażu.
  const nettoMontazMagazynu = naNetto(montazMagazynuZl)
  const nettoMontaz = nettoMontazPv + nettoMontazMagazynu
  const nettoRazem = nettoTowar + nettoMontaz

  const bruttoTowar = nettoTowar * (1 + stawkaSprzetu / 100)
  const bruttoMontaz = nettoMontaz * (1 + stawkaMontazu / 100)

  // Marża liczona od NETTO całości (procentowo) albo wprost z tabeli (kwotowo),
  // ale doliczana jako BRUTTO — nie nakładamy na nią podatku, bo nie jest
  // osobną pozycją na fakturze, tylko częścią ceny usługi.
  // Marża za magazyn (runda 6): próg po pojemności w kWh, ten sam tryb co marża instalacji. Procentowo od netto
  // magazynu z jego montażem (`magazynZl` to cena magazynu, która siedzi już w `sprzetZl`).
  const nettoMagazynu = Number(kwh) > 0 ? naNetto(magazynZl) : 0
  const marzaMagazynu = Number(kwh) > 0
    ? marzaNetto({ progi: progiMarzyMagazynu, tryb: trybMarzy, kwp: kwh, nettoZl: nettoMagazynu + nettoMontazMagazynu })
    : 0
  // Podstawa marży instalacji bez montażu magazynu: taka sama jak przed rundą 6, więc firma z marżą procentową
  // nie dostaje innej kwoty tylko dlatego, że doszła pozycja magazynu. Gdy firma ustawiła marżę za magazyn
  // (procentową), magazyn wypada z podstawy marży instalacji, żeby nie brał procentu dwa razy.
  const bezMagazynu = trybMarzy === 'percent' && marzaMagazynu > 0 ? nettoMagazynu : 0
  const marzaPv = marzaNetto({ progi: progiMarzy, tryb: trybMarzy, kwp, nettoZl: nettoRazem - nettoMontazMagazynu - bezMagazynu })
  const marza = marzaPv + marzaMagazynu

  // Usługi dodatkowe firmy: cena netto i WŁASNA stawka VAT każdej usługi, poza marżą.
  const listaUslug = Array.isArray(uslugi) ? uslugi : []
  const nettoUslug = listaUslug.reduce((s, u) => s + (Number(u?.netto) || 0), 0)
  const vatUslug = listaUslug.reduce((s, u) => s + (Number(u?.netto) || 0) * ((Number(u?.vat) || 0) / 100), 0)

  const bruttoKoncowe = bruttoTowar + bruttoMontaz + marza + nettoUslug + vatUslug

  return {
    nettoZl: nettoRazem + nettoUslug,
    nettoTowarZl: nettoTowar,
    nettoMontazZl: nettoMontaz,
    nettoMontazMagazynuZl: nettoMontazMagazynu,
    marzaZl: marza,
    marzaInstalacjiZl: marzaPv,
    marzaMagazynuZl: marzaMagazynu,
    nettoUslugZl: nettoUslug,
    vatZl: (bruttoTowar - nettoTowar) + (bruttoMontaz - nettoMontaz) + vatUslug,
    bruttoZl: Math.round(bruttoKoncowe),
    stawkaVatSprzetu: stawkaSprzetu,
    stawkaVatMontazu: stawkaMontazu,
  }
}

/**
 * Magazyn zestawu (runda 6): pojemność w kWh i cena magazynów (brutto, ceny sklepów) z pozycji `battery`.
 * Pojemność z podsumowania silnika, a gdy go brak, z pozycji (pojemność modułu razy liczba sztuk).
 * Bez pozycji magazynu (np. odznaczony na ekranie potwierdzenia): `{ kwh: null, cenaZl: 0 }`.
 */
export const magazynZPozycji = (items, summary = null) => {
  const baterie = (Array.isArray(items) ? items : []).filter((i) => i?.category === 'battery')
  if (!baterie.length) return { kwh: null, cenaZl: 0 }
  const cenaZl = baterie.reduce((s, i) => s + (Number(i.line_price_pln) || 0), 0)
  return { kwh: pojemnoscZestawuKwh(summary, baterie), cenaZl }
}

/** Montaż magazynu z tabeli firmy (zł/kWh razy pojemność) albo null, gdy nie ma magazynu albo stawki. */
export const montazMagazynuZProgow = (progi, kwh) => {
  const stawka = stawkaMontazuZProgow(progi, kwh)
  return stawka != null && stawka > 0 ? { stawka, kwotaZl: Math.round(stawka * Number(kwh)) } : null
}

/**
 * Usługi dodatkowe firmy wybrane dla klienta: pozycje z `extra_services` w kolejności firmy, z kwotą brutto
 * w stawce VAT usługi. Id spoza listy firmy (usługa usunięta w ustawieniach) pomijamy.
 */
export const wybraneUslugi = (uslugiFirmy, ids) => {
  const wybrane = new Set(Array.isArray(ids) ? ids : [])
  return (Array.isArray(uslugiFirmy) ? uslugiFirmy : [])
    .filter((u) => u && wybrane.has(u.id))
    .map((u) => {
      const netto = Number(u.netto) || 0
      const vat = Number(u.vat) || 0
      return { id: u.id, nazwa: u.nazwa, netto, vat, brutto: Math.round(netto * (1 + vat / 100) * 100) / 100 }
    })
}
